import { Fragment, useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useAuditLogs, useAuditFacets } from './hooks/useAuditLogs';
import Button from '../../shared/components/Button';
import type { AuditEvent, AuditFilters, AuditStatusFilter } from './types';
import { ExportButton } from "../../shared/components/ExportButton";

function formatDateTime(dateStr: string) {
  return new Date(dateStr).toLocaleString('en-GB', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
  });
}

function useDebounce(value: string, delay: number): string {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const timer = setTimeout(() => setDebounced(value), delay);
    return () => clearTimeout(timer);
  }, [value, delay]);
  return debounced;
}

const PAGE_SIZE = 20;

const INPUT_CLS =
  'border border-border rounded-lg px-3 py-2 text-sm bg-card text-foreground focus:outline-none focus:ring-2 focus:ring-purple-300';

const DETAIL_LABEL_CLS = 'text-xs font-medium text-muted-foreground uppercase tracking-wide mb-0.5';

type StatusSelection = '' | AuditStatusFilter;

const STATUS_OPTIONS: { label: string; value: StatusSelection }[] = [
  { label: 'All statuses', value: '' },
  { label: 'Success', value: 'success' },
  { label: 'Failure', value: 'failure' },
];

function StatusCodeBadge({ statusCode }: { statusCode: number }) {
  const style = statusCode < 400 ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700';
  return (
    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${style}`}>
      {statusCode}
    </span>
  );
}

function EventDetailRow({ event }: { event: AuditEvent }) {
  return (
    <tr className="bg-muted/40">
      <td colSpan={6} className="px-6 py-4">
        <dl className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-3 text-sm">
          <div>
            <dt className={DETAIL_LABEL_CLS}>Request</dt>
            <dd className="text-foreground font-mono break-all">{event.method} {event.path}</dd>
          </div>
          <div>
            <dt className={DETAIL_LABEL_CLS}>IP</dt>
            <dd className="text-foreground font-mono">{event.ip ?? '—'}</dd>
          </div>
          <div>
            <dt className={DETAIL_LABEL_CLS}>User Agent</dt>
            <dd className="text-muted-foreground break-all">{event.userAgent ?? '—'}</dd>
          </div>
          <div>
            <dt className={DETAIL_LABEL_CLS}>Tenant</dt>
            <dd className="text-foreground font-mono break-all">{event.tenantId ?? '—'}</dd>
          </div>
          <div className="md:col-span-2">
            <dt className={DETAIL_LABEL_CLS}>Message</dt>
            <dd className="text-foreground">{event.message ?? '—'}</dd>
          </div>
        </dl>
        {event.details && (
          <div className="mt-3">
            <p className={DETAIL_LABEL_CLS}>Details</p>
            <pre className="text-xs text-foreground bg-card border border-border rounded-lg p-3 overflow-x-auto">
              {JSON.stringify(event.details, null, 2)}
            </pre>
          </div>
        )}
      </td>
    </tr>
  );
}

export default function AuditLogs() {
  // PE-2147 — deep links (e.g. Departments → "History" →
  // /audit-logs?resource=DEPARTMENT&service=watchman-service) seed the initial
  // filter state; the page owns the filters from then on.
  const [searchParams] = useSearchParams();
  const initial = (key: string) => searchParams.get(key) ?? '';
  const [resource, setResource] = useState(initial('resource'));
  const [action, setAction] = useState(initial('action'));
  const [service, setService] = useState(initial('service'));
  const [status, setStatus] = useState<StatusSelection>('');
  const [actorEmail, setActorEmail] = useState(initial('actorEmail'));
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const [page, setPage] = useState(1);
  const [expandedId, setExpandedId] = useState<string | null>(null);

  const debouncedActorEmail = useDebounce(actorEmail, 300);

  // A committed (debounced) email search is a filter change — jump back to page 1.
  useEffect(() => {
    setPage(1);
  }, [debouncedActorEmail]);

  const { data: facets } = useAuditFacets();

  const filters: AuditFilters = {
    page,
    limit: PAGE_SIZE,
    ...(resource ? { resource } : {}),
    ...(action ? { action } : {}),
    ...(service ? { service } : {}),
    ...(status ? { status } : {}),
    ...(debouncedActorEmail ? { actorEmail: debouncedActorEmail } : {}),
    ...(from ? { from } : {}),
    ...(to ? { to } : {}),
  };
  const { data, isLoading, error, isFetching } = useAuditLogs(filters);

  const events: AuditEvent[] = data?.data ?? [];
  const total = data?.meta.total ?? 0;
  const totalPages = Math.max(1, data?.meta.totalPages ?? 1);

  const hasActiveFilters = Boolean(resource || action || service || status || actorEmail || from || to);

  const resetTo = <T extends string>(setter: (v: T) => void) => (value: T) => {
    setter(value);
    setPage(1);
  };

  const clearFilters = () => {
    setResource('');
    setAction('');
    setService('');
    setStatus('');
    setActorEmail('');
    setFrom('');
    setTo('');
    setPage(1);
  };

  return (
    <div className="p-6 max-w-7xl mx-auto">
      <div className="mb-6 flex items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold text-foreground">Audit Logs</h1>
          <p className="text-sm text-muted-foreground mt-1">
            Who did what, across the platform. Scoped to your permissions.
          </p>
        </div>
        {/* PE-2127 — export the audit trail. */}
        <ExportButton endpoint="/audit/export" filenameBase="audit-logs" className="shrink-0" />
      </div>

      {/* Filters */}
      <div className="flex flex-wrap items-end gap-3 mb-4">
        <div>
          <label className="block text-xs font-medium text-muted-foreground mb-1">Resource</label>
          <select
            className={INPUT_CLS}
            value={resource}
            onChange={(e) => resetTo(setResource)(e.target.value)}
          >
            <option value="">All resources</option>
            {(facets?.resources ?? []).map((r) => <option key={r} value={r}>{r}</option>)}
          </select>
        </div>
        <div>
          <label className="block text-xs font-medium text-muted-foreground mb-1">Action</label>
          <select
            className={INPUT_CLS}
            value={action}
            onChange={(e) => resetTo(setAction)(e.target.value)}
          >
            <option value="">All actions</option>
            {(facets?.actions ?? []).map((a) => <option key={a} value={a}>{a}</option>)}
          </select>
        </div>
        <div>
          <label className="block text-xs font-medium text-muted-foreground mb-1">Service</label>
          <select
            className={INPUT_CLS}
            value={service}
            onChange={(e) => resetTo(setService)(e.target.value)}
          >
            <option value="">All services</option>
            {(facets?.services ?? []).map((s) => <option key={s} value={s}>{s}</option>)}
          </select>
        </div>
        <div>
          <label className="block text-xs font-medium text-muted-foreground mb-1">Status</label>
          <select
            className={INPUT_CLS}
            value={status}
            onChange={(e) => resetTo<StatusSelection>(setStatus)(e.target.value as StatusSelection)}
          >
            {STATUS_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
          </select>
        </div>
        <div>
          <label className="block text-xs font-medium text-muted-foreground mb-1">Actor email</label>
          <input
            type="text"
            className={INPUT_CLS}
            placeholder="Search by email…"
            value={actorEmail}
            onChange={(e) => setActorEmail(e.target.value)}
          />
        </div>
        <div>
          <label className="block text-xs font-medium text-muted-foreground mb-1">From</label>
          <input
            type="date"
            className={INPUT_CLS}
            value={from}
            onChange={(e) => resetTo(setFrom)(e.target.value)}
          />
        </div>
        <div>
          <label className="block text-xs font-medium text-muted-foreground mb-1">To</label>
          <input
            type="date"
            className={INPUT_CLS}
            value={to}
            onChange={(e) => resetTo(setTo)(e.target.value)}
          />
        </div>
        {hasActiveFilters && (
          <Button variant="outline" size="sm" onClick={clearFilters}>
            Clear filters
          </Button>
        )}
      </div>

      {isLoading && <div className="text-center py-16 text-muted-foreground">Loading audit events…</div>}
      {error && <div className="text-center py-16 text-red-500">Failed to load audit events.</div>}
      {!isLoading && !error && events.length === 0 && (
        <div className="text-center py-16 text-muted-foreground">No audit events match these filters.</div>
      )}

      {!isLoading && !error && events.length > 0 && (
        <div className="bg-card rounded-2xl border border-border overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b border-border bg-muted text-left">
                <th className="px-6 py-3 text-xs font-medium text-muted-foreground uppercase tracking-wide">Time</th>
                <th className="px-6 py-3 text-xs font-medium text-muted-foreground uppercase tracking-wide">Actor</th>
                <th className="px-6 py-3 text-xs font-medium text-muted-foreground uppercase tracking-wide">Action</th>
                <th className="px-6 py-3 text-xs font-medium text-muted-foreground uppercase tracking-wide">Service</th>
                <th className="px-6 py-3 text-xs font-medium text-muted-foreground uppercase tracking-wide">Status</th>
                <th className="px-6 py-3 text-xs font-medium text-muted-foreground uppercase tracking-wide">Path</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-50">
              {events.map((e) => (
                <Fragment key={e.id}>
                  <tr
                    className="hover:bg-muted transition-colors cursor-pointer"
                    onClick={() => setExpandedId((current) => (current === e.id ? null : e.id))}
                  >
                    <td className="px-6 py-4 text-sm text-muted-foreground whitespace-nowrap">{formatDateTime(e.occurredAt)}</td>
                    <td className="px-6 py-4 text-sm">
                      <span className="block text-foreground max-w-[200px] truncate" title={e.actorEmail ?? undefined}>
                        {e.actorEmail ?? e.actorId ?? '—'}
                      </span>
                      {e.actorRole && (
                        <span className="block text-xs text-muted-foreground">{e.actorRole}</span>
                      )}
                    </td>
                    <td className="px-6 py-4">
                      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-muted text-foreground whitespace-nowrap">
                        {e.resource} · {e.action}
                      </span>
                    </td>
                    <td className="px-6 py-4 text-sm text-muted-foreground">{e.service}</td>
                    <td className="px-6 py-4"><StatusCodeBadge statusCode={e.statusCode} /></td>
                    <td
                      className="px-6 py-4 text-sm text-muted-foreground font-mono max-w-[240px] truncate"
                      title={`${e.method} ${e.path}`}
                    >
                      {e.method} {e.path}
                    </td>
                  </tr>
                  {expandedId === e.id && <EventDetailRow event={e} />}
                </Fragment>
              ))}
            </tbody>
          </table>

          {/* Pagination */}
          <div className="flex items-center justify-between px-6 py-3 border-t border-border">
            <span className="text-xs text-muted-foreground">
              {total} event{total === 1 ? '' : 's'} · page {page} of {totalPages}{isFetching ? ' · …' : ''}
            </span>
            <div className="flex gap-2">
              <button
                onClick={() => setPage((n) => Math.max(1, n - 1))}
                disabled={page <= 1}
                className="px-3 py-1.5 text-sm border border-border rounded-lg disabled:opacity-40 hover:bg-muted"
              >
                Previous
              </button>
              <button
                onClick={() => setPage((n) => Math.min(totalPages, n + 1))}
                disabled={page >= totalPages}
                className="px-3 py-1.5 text-sm border border-border rounded-lg disabled:opacity-40 hover:bg-muted"
              >
                Next
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
